import { useTheme } from "next-themes";
import { Toaster as Sonner, toast } from "sonner";

type ToasterProps = React.ComponentProps<typeof Sonner>;

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      duration={3000}
      toastOptions={{
        classNames: {
          // VA-141: notifications use the prototype's pale-orange style.
          //
          // A TINT over the themed surface, not a fixed pale fill. A solid pale
          // orange would need its own dark-mode variant to stay readable, and every
          // --brand-* variable is declared identically in both blocks by design
          // (see docs/rules/brand-colors.md), so a tint tracks the surface underneath
          // and reads correctly in light and dark without a second definition.
          //
          // The tint must sit on an OPAQUE base. `bg-brand-gold/10` alone is a 10%
          // wash over whatever happens to be behind the toast — so page content shows
          // straight through it. Kate (VA-215) hit exactly that: the refusal message
          // was correct but unreadable, with a button from the page bleeding through
          // the text.
          //
          // `bg-background` paints the opaque surface and the flat gradient paints the
          // tint on top of it, because background-image layers above background-color.
          // Two properties, one element, still one definition for both themes.
          toast:
            "group toast group-[.toaster]:bg-background group-[.toaster]:bg-gradient-to-r " +
            "group-[.toaster]:from-brand-gold/10 group-[.toaster]:to-brand-gold/10 " +
            "group-[.toaster]:text-foreground group-[.toaster]:border-brand-gold/40 group-[.toaster]:shadow-lg",
          description: "group-[.toast]:text-muted-foreground",
          actionButton: "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton: "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
          // Errors keep destructive styling — the point of the brand tint is routine
          // notifications, and tinting a failure gold would read as success.
          error:
            "group toast group-[.toaster]:bg-background group-[.toaster]:bg-gradient-to-r " +
            "group-[.toaster]:from-destructive/10 group-[.toaster]:to-destructive/10 " +
            "group-[.toaster]:text-foreground group-[.toaster]:border-destructive/40 group-[.toaster]:shadow-lg",
        },
      }}
      {...props}
    />
  );
};

export { Toaster, toast };
